import { Tariffs } from '@cookspace-shared-contracts/core';

import { baseApi } from '@/api/baseApi';
import { parseWithSchema } from '@/lib/safeParse';

/**
 * RTK Query endpoints для admin-backend /tariffs/*.
 * admin-backend живёт на отдельном порту 5002 — префикс /admin не используется.
 */
export const tariffsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        listTariffs: build.query<
            Tariffs.GetTariffsResponse,
            Tariffs.GetTariffsRequest | void
        >({
            query: (params) => {
                const search: Record<string, string> = {};
                if (params?.isActive !== undefined) {
                    search.isActive = params.isActive ? 'true' : 'false';
                }
                return {
                    url: '/tariffs',
                    method: 'GET',
                    params: search
                };
            },
            transformResponse: (raw: unknown): Tariffs.GetTariffsResponse =>
                parseWithSchema(Tariffs.GetTariffsResponseSchema, raw),
            providesTags: (result) =>
                result
                    ? [
                        ...result.items.map((t) => ({
                            type: 'Tariff' as const,
                            id: t.id
                        })),
                        { type: 'Tariff' as const, id: 'LIST' }
                    ]
                    : [{ type: 'Tariff' as const, id: 'LIST' }]
        }),

        createTariff: build.mutation<
            Tariffs.CreateTariffResponse,
            Tariffs.CreateTariffRequest
        >({
            query: (body) => ({
                url: '/tariffs',
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): Tariffs.CreateTariffResponse =>
                parseWithSchema(Tariffs.CreateTariffResponseSchema, raw),
            invalidatesTags: [{ type: 'Tariff', id: 'LIST' }]
        }),

        updateTariff: build.mutation<
            Tariffs.UpdateTariffResponse,
            { id: string; body: Tariffs.UpdateTariffRequest }
        >({
            query: ({ id, body }) => ({
                url: `/tariffs/${id}`,
                method: 'PATCH',
                body
            }),
            transformResponse: (raw: unknown): Tariffs.UpdateTariffResponse =>
                parseWithSchema(Tariffs.UpdateTariffResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'Tariff', id: arg.id },
                { type: 'Tariff', id: 'LIST' }
            ]
        }),

        deleteTariff: build.mutation<Tariffs.DeleteTariffResponse, { id: string }>({
            query: ({ id }) => ({
                url: `/tariffs/${id}`,
                method: 'DELETE'
            }),
            transformResponse: (raw: unknown): Tariffs.DeleteTariffResponse =>
                parseWithSchema(Tariffs.DeleteTariffResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'Tariff', id: arg.id },
                { type: 'Tariff', id: 'LIST' }
            ]
        }),

        reorderTariffs: build.mutation<
            Tariffs.ReorderTariffsResponse,
            Tariffs.ReorderTariffsRequest
        >({
            query: (body) => ({
                url: '/tariffs/reorder',
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): Tariffs.ReorderTariffsResponse =>
                parseWithSchema(Tariffs.ReorderTariffsResponseSchema, raw),
            invalidatesTags: [{ type: 'Tariff', id: 'LIST' }]
        })
    })
});

export const {
    useListTariffsQuery,
    useCreateTariffMutation,
    useUpdateTariffMutation,
    useDeleteTariffMutation,
    useReorderTariffsMutation
} = tariffsApi;
